import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import { Badge } from '@shared/components/ui/shadcn/badge';
import { Checkbox } from '@shared/components/ui/shadcn/checkbox';
import { Label } from '@shared/components/ui/shadcn/label';
import { ScrollArea } from '@shared/components/ui/shadcn/scroll-area';
import { AlertCircle, CheckCircle2, ClipboardCheck, ExternalLink, Package } from 'lucide-react';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EntryReceiptRequirement } from '../../../models/Entry';
import { NON_COMPLIANCE_LABELS, NON_COMPLIANCE_REQUIREMENT_CODES } from '../../models/Receive';

export interface FinalizeReceiptModalRef {
  open: (
    receiptCode: string,
    totalMovements: number,
    dispatchedMovements: number,
    requirements: EntryReceiptRequirement[],
    onConfirm: () => Promise<void>,
    onManageRequirements?: () => void,
  ) => void;
  close: () => void;
}

export const FinalizeReceiptModal = forwardRef<FinalizeReceiptModalRef>((_, ref) => {
  const { t } = useTranslation('merchandiseV3');
  const modalRef = useRef<ModalCenterRef>(null);
  const [receiptCode, setReceiptCode] = useState<string>('');
  const [totalMovements, setTotalMovements] = useState<number>(0);
  const [dispatchedMovements, setDispatchedMovements] = useState<number>(0);
  const [requirements, setRequirements] = useState<EntryReceiptRequirement[]>([]);
  const [reviewedCodes, setReviewedCodes] = useState<Set<string>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const confirmCallbackRef = useRef<(() => Promise<void>) | null>(null);
  const manageRequirementsCallbackRef = useRef<(() => void) | null>(null);

  const registeredCodes = useMemo(
    () =>
      new Set(
        requirements
          .map((r) => r.requirement_code)
          .filter((code) =>
            NON_COMPLIANCE_REQUIREMENT_CODES.includes(code as (typeof NON_COMPLIANCE_REQUIREMENT_CODES)[number]),
          ),
      ),
    [requirements],
  );

  const reviewedCount = reviewedCodes.size;
  const totalComplianceItems = NON_COMPLIANCE_REQUIREMENT_CODES.length;
  const allComplianceReviewed = reviewedCount === totalComplianceItems;

  const resetState = useCallback(() => {
    setReviewedCodes(new Set());
    setIsProcessing(false);
  }, []);

  useImperativeHandle(ref, () => ({
    open: (
      code: string,
      total: number,
      dispatched: number,
      requirementsData: EntryReceiptRequirement[],
      onConfirm: () => Promise<void>,
      onManageRequirements?: () => void,
    ) => {
      setReceiptCode(code);
      setTotalMovements(total);
      setDispatchedMovements(dispatched);
      setRequirements(requirementsData);
      resetState();
      confirmCallbackRef.current = onConfirm;
      manageRequirementsCallbackRef.current = onManageRequirements ?? null;
      modalRef.current?.openModal();
    },
    close: () => {
      modalRef.current?.closeModal();
      resetState();
    },
  }));

  const handleToggleReviewed = (code: string, checked: boolean) => {
    if (isProcessing) return;

    setReviewedCodes((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(code);
      } else {
        next.delete(code);
      }
      return next;
    });
  };

  const handleManageRequirements = () => {
    if (isProcessing) return;
    manageRequirementsCallbackRef.current?.();
  };

  const handleConfirm = async () => {
    if (!allComplianceReviewed || !confirmCallbackRef.current) return;

    setIsProcessing(true);
    try {
      await confirmCallbackRef.current();
      modalRef.current?.closeModal();
    } catch (error) {
      console.error('Error al finalizar recepción:', error);
      modalRef.current?.closeModal();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      modalRef.current?.closeModal();
      resetState();
    }
  };

  return (
    <ModalCenter
      ref={modalRef}
      title={t('receive.finalize.modal.title', 'Finalizar Recepción')}
      type="warning"
      width="lg"
      onCloseModal={resetState}
    >
      <div className="space-y-6">
        {/* Header con información */}
        <div className="bg-muted/30 rounded-xl p-4 border border-border">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <Package className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">{receiptCode}</h3>
              <p className="text-sm text-muted-foreground">
                {dispatchedMovements} {t('receive.finalize.modal.of', 'de')} {totalMovements}{' '}
                {t('receive.finalize.modal.movements_dispatched', 'movimientos despachados')}
              </p>
            </div>
          </div>
        </div>

        {/* Revisión obligatoria de incumplimientos */}
        <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-4 space-y-3">
          <div className="flex items-start gap-3">
            <ClipboardCheck className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-foreground">
                {t('receive.finalize.modal.compliance_review.title', 'Revisión obligatoria de incumplimientos')}
              </h4>
              <p className="text-xs text-muted-foreground mt-1">
                {t(
                  'receive.finalize.modal.compliance_review.description',
                  'Debes revisar cada punto antes de finalizar. Si detectas un incumplimiento, regístralo con evidencia.',
                )}
              </p>
              <p className="text-xs font-medium text-foreground mt-2">
                {t('receive.finalize.modal.compliance_review.progress', '{{reviewed}} de {{total}} revisados', {
                  reviewed: reviewedCount,
                  total: totalComplianceItems,
                })}
              </p>
            </div>
            {manageRequirementsCallbackRef.current && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleManageRequirements}
                disabled={isProcessing}
                className="flex-shrink-0"
                data-testid="finalize-manage-requirements-button"
              >
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                {t('receive.finalize.modal.compliance_review.manage', 'Gestionar')}
              </Button>
            )}
          </div>

          <ScrollArea className="h-[220px] pr-3">
            <div className="space-y-2">
              {NON_COMPLIANCE_REQUIREMENT_CODES.map((code) => {
                const isRegistered = registeredCodes.has(code);
                const isReviewed = reviewedCodes.has(code);
                const label = NON_COMPLIANCE_LABELS[code];

                return (
                  <div
                    key={code}
                    className={`rounded-lg border p-3 transition-colors ${
                      isReviewed
                        ? 'border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-950/20'
                        : 'border-border bg-background'
                    }`}
                    data-testid={`finalize-compliance-item-${code}`}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id={`finalize-review-${code}`}
                        checked={isReviewed}
                        onCheckedChange={(checked) => handleToggleReviewed(code, checked === true)}
                        disabled={isProcessing}
                        className="mt-0.5 flex-shrink-0"
                        data-testid={`finalize-compliance-checkbox-${code}`}
                      />
                      <div className="flex-1 min-w-0">
                        <Label
                          htmlFor={`finalize-review-${code}`}
                          className="cursor-pointer text-sm font-medium text-foreground leading-snug"
                        >
                          {label}
                        </Label>
                        <div className="mt-1.5">
                          <Badge
                            variant={isRegistered ? 'default' : 'secondary'}
                            className={
                              isRegistered
                                ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 hover:bg-orange-100 dark:hover:bg-orange-900/30'
                                : ''
                            }
                          >
                            {isRegistered
                              ? t('receive.finalize.modal.compliance_review.registered', 'Registrado')
                              : t('receive.finalize.modal.compliance_review.not_registered', 'Sin registrar')}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          {!allComplianceReviewed && (
            <p className="text-xs text-orange-700 dark:text-orange-300">
              {t(
                'receive.finalize.modal.compliance_review.pending_hint',
                'Marca cada punto como revisado para poder finalizar la recepción.',
              )}
            </p>
          )}
        </div>

        {/* Advertencia importante */}
        <Alert className="bg-orange-50 dark:bg-orange-950/20 border-2 border-orange-300 dark:border-orange-700">
          <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
          <AlertDescription className="text-sm text-orange-700 dark:text-orange-300">
            <strong>{t('receive.finalize.modal.warning_title', '¡Importante!')}</strong>{' '}
            {t('receive.finalize.modal.warning_text', 'Al finalizar esta recepción:')}
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>{t('receive.finalize.modal.warning_1', 'No podrás procesar más productos en esta recepción')}</li>
              <li>
                {t('receive.finalize.modal.warning_2', 'Todos los movimientos aceptados deben estar despachados')}
              </li>
              <li>
                <strong>{t('receive.finalize.modal.warning_3', 'Esta acción no se puede deshacer')}</strong>
              </li>
            </ul>
          </AlertDescription>
        </Alert>

        {/* Confirmación */}
        <div className="bg-muted/20 rounded-lg p-4 border border-border">
          <p className="text-sm text-foreground text-center">
            {t('receive.finalize.modal.confirm_text', '¿Estás seguro de que deseas finalizar esta recepción?')}
          </p>
        </div>

        {/* Botones */}
        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button variant="outline" size="lg" onClick={handleClose} disabled={isProcessing} className="min-w-[120px]">
            {t('receive.finalize.modal.cancel', 'Cancelar')}
          </Button>
          <Button
            size="lg"
            onClick={handleConfirm}
            disabled={isProcessing || !allComplianceReviewed}
            className="min-w-[180px] bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50"
            data-testid="finalize-receipt-confirm-button"
            title={
              !allComplianceReviewed
                ? t(
                    'receive.finalize.modal.compliance_review.confirm_disabled',
                    'Debes revisar todos los incumplimientos antes de finalizar',
                  )
                : undefined
            }
          >
            {isProcessing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-background border-t-transparent mr-2"></div>
                {t('receive.finalize.modal.finalizing', 'Finalizando...')}
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5 mr-2" />
                {t('receive.finalize.modal.confirm', 'Confirmar Finalización')}
              </>
            )}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

FinalizeReceiptModal.displayName = 'FinalizeReceiptModal';
